import React, { Fragment, useEffect, useMemo, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { notification } from 'src/components/Notification';
import { getCompanyByType } from 'src/utils/form/job';
import { getFixedValues } from 'src/components/JobDatasheet/utils';
import { QuestionCheckbox } from 'src/components/JobDatasheet/QuestionCheckbox';
import { QuestionCurrency } from 'src/components/JobDatasheet/QuestionCurrency';
import { QuestionDate } from 'src/components/JobDatasheet/QuestionDate';
import { QuestionDropdown } from 'src/components/JobDatasheet/QuestionDropdown';
import { QuestionDependentDropdown } from 'src/components/JobDatasheet/QuestionDependentDropdown';
import { QuestionFile } from 'src/components/JobDatasheet/QuestionFile';
import { QuestionFixed } from 'src/components/JobDatasheet/QuestionFixed';
import { QuestionMonth } from 'src/components/JobDatasheet/QuestionMonth';
import { QuestionNumber } from 'src/components/JobDatasheet/QuestionNumber';
import { QuestionPercent } from 'src/components/JobDatasheet/QuestionPercent';
import { QuestionRelation } from 'src/components/JobDatasheet/QuestionRelation';
import { QuestionTableDefaultProps, QuestionTablePropTypes } from 'src/components/JobDatasheet/propTypes';
import { QuestionTableHeader } from 'src/components/JobDatasheet/QuestionTableHeader';
import { QuestionText } from 'src/components/JobDatasheet/QuestionText';
import { QuestionYear } from 'src/components/JobDatasheet/QuestionYear';
import { toFullUtcDateString } from 'src/utils/format/date';
import { useGetMobilePhotoRecordData } from 'src/hooks/job';
import JobDatasheetLanguageContext from 'src/components/JobDatasheet/context/JobDatasheetLanguageContext';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';

export const QuestionTable = ({
  errors,
  flash,
  isAdditionalReportingDocuments,
  isRectifiedQuestion,
  job,
  onChange,
  question,
  readonly,
  value,
  disableChange,
  showError
}) => {
  const {
    tableSettings: { columns, enableMultipleRows, fixedValues },
    translations,
  } = question;
  const { t } = useTranslation();

  const { selectedLanguage } = useContext(JobDatasheetLanguageContext);

  const getOptionLabel = (index, defaultValue) => {
    if (!selectedLanguage) {
      return defaultValue;
    }

    return (translations || []).find(c => c.languageId === selectedLanguage)?.options?.[index] || defaultValue;
  };

  const getTranslationOptions = (fromIndex, toIndex) => {
    if (!selectedLanguage) {
      return [];
    }

    return (translations || []).find(c => c.languageId === selectedLanguage)?.options?.slice(fromIndex, toIndex) || [];
  };

  const fixedColumns = columns.filter(({ type }) => type === 'fixed');

  const fixedColumnTranslationOptions = [];
  let fixedColumnTranslationOptionIndex = 0;
  columns.forEach(({ options }) => {
    fixedColumnTranslationOptionIndex += 1 + (options || []).length;
  });
  fixedColumns.forEach(({ name }) => {
    const columnFixedValues = fixedValues.filter(({ columnName }) => columnName === name);
    fixedColumnTranslationOptions.push({
      name,
      from: fixedColumnTranslationOptionIndex,
    });
    fixedColumnTranslationOptionIndex += columnFixedValues.length;
  });

  const { execute: getPhotoRecordsFromDB, photoRecordsData = [] } = useGetMobilePhotoRecordData(job?.id);

  const showPhotoFormPreviewRow = useMemo(
    () => photoRecordsData.some(record => record.viewInPhotoForm === true && record.isPrimary),
    [photoRecordsData]);

  useEffect(() => {
    if (job?.id) {
     getPhotoRecordsFromDB();
    }
  }, [job?.id]);

  const companies = job?.jobCompanies?.filter(c => !!c.company) || [];
  const factory = getCompanyByType(companies, 'Factory');
  const factoryName = factory ? factory.company.currentVersion.name : '';

  const jobStartDate = job?.auditStartAt ? toFullUtcDateString(job.auditStartAt) : null;
  const jobEndDate = job?.auditEndAt ? toFullUtcDateString(job.auditEndAt) : null;
  const jobStartDay = jobStartDate ? jobStartDate.split('-')[0] : '';
  const jobStartMonth = jobStartDate ? jobStartDate.split('-')[1] : '';
  const jobStartYear = jobStartDate ? jobStartDate.split('-').pop() : '';
  const jobEndDay = jobEndDate ? jobEndDate.split('-')[0] : '';

  if (value === null || value === undefined) {
    const groupedFixedValues = getFixedValues(fixedColumns, fixedValues);
    const rowsCount =
      Object.values(groupedFixedValues)
        .map(c => c.length)
        .sort()
        .reverse()[0] || 1;

    // eslint-disable-next-line no-param-reassign
    value = {
      type: 'table',
      rows: [...Array(rowsCount).keys()].map(rowIndex => {
        return columns.map(({ name, type }) => {
          if (type === 'fixed') {
            return {
              column: name,
              type: 'fixed',
              value: groupedFixedValues[name]
                ? groupedFixedValues[name][rowIndex]
                : '',
            };
          }

          return {
            column: name,
          };
        });
      }),
    };
  }

  const addRow = () => {
    if(readonly){
      notification.error(readonly);
      return;
    }

    onChange({
      rows: [
        ...(value ? value.rows : []),
        columns.map(column => ({
          column: column.name,
        })),
      ],
      type: 'table',
    });
  };

  const removeRow = index => {
    if(readonly){
      notification.error(readonly);
      return;
    }

    const newRows = value.rows;
    if (newRows.length > 1) {
      newRows.splice(index, 1);
    }

    onChange({
      rows: [...newRows],
      type: 'table',
    });
  };

  const onCellChange = (index, { name: columnName, type: columnType, number: columnNumber }, newValue) => {
    const newRows = [...value.rows];
    newRows[index] = newRows[index].map(v => {
      if (v.column === columnName) {
        return {
          columnName,
          columnNumber,
          ...newValue,
        };
      }

      return v;
    });

    /**
     * passing second default param shouldShowError if column type is file or not
     * prevents unnecessary error toast messages when
     * attachments are loaded on page tab load
     */
    if(!readonly){
      onChange({
        rows: newRows,
        type: 'table',
      }, columnType.toString().trim() !== 'file');
    }
  };

  const getDependentCellResult = (dependsOnColumn, rowIndex) => {
    return (value.rows[rowIndex].filter((column, index) => Number.isNaN(column.columnNumber) ? column.columnNumber === dependsOnColumn : index === dependsOnColumn)[0] || null);
  }

  const getParentColumn = (activeColumn) => {
    return columns.filter((column) => column.number === activeColumn.dependsOn)[0] || null;
  }

  const questionTypeMapping = {
    checkbox: QuestionCheckbox,
    currency: QuestionCurrency,
    date: QuestionDate,
    dropdown: QuestionDropdown,
    dependentDropdown: QuestionDependentDropdown,
    file: QuestionFile,
    fixed: QuestionFixed,
    month: QuestionMonth,
    number: QuestionNumber,
    percent: QuestionPercent,
    relation: QuestionRelation,
    text: QuestionText,
    year: QuestionYear,
  };

  return (
    <s.TableFieldsWrapper
    flash={flash}
    >
      <QuestionTableHeader question={question} getOptionLabel={getOptionLabel} isError={showError && errors.length>0} />
      {(value || { rows: [] }).rows?.map((row, index) => {
        let translationIndex = 0;

        return (
          <s.TableFields key={index}>
            {fixedColumns.length === 0 && enableMultipleRows && index !== 0 ? (
              <s.TableFieldsRemove
                onClick={() => removeRow(index)}
              />
            ) : null}
            {columns.map((column, index2) => {
              const QuestionTypeForm = questionTypeMapping[column.type];
              const cellValue = (value ? value.rows : [])[index].find(
                v => v.column === column.name,
              );

              if (cellValue !== undefined && column.type === 'fixed') {
                const fixedOptions = fixedColumnTranslationOptions.find(c => c.name === column.name);
                if (fixedOptions) {
                  const groupedFixedValues = getFixedValues(fixedColumns, fixedValues);
                  cellValue.value = getOptionLabel(fixedOptions.from + index, groupedFixedValues[column.name]?.[index]);
                }
              }
              let {options} = column;
              if(column.type === "dependentDropdown"){
                options = column.dependentDropdownOptions;
              }
              const translationOptions = getTranslationOptions(translationIndex + 1, translationIndex + 1 + (options || []).length)
              translationIndex += 1 + (options || []).length;
              
              return (
                <s.TableField
                  key={`${index}:${index2}`}
                >
                  <QuestionTypeForm
                    inTable={true}
                    job={job}
                    isRectifiedQuestion={isRectifiedQuestion}
                    onChange={!disableChange && ((v) => onCellChange(index, column, v))}
                    skipPlaceholder={true}
                    question={{
                      currencySettings: column,
                      dropdownSettings: column,
                      dependentDropdownSettings: { ...column, options: column.dependentDropdownOptions },
                      fileSettings: column,
                      numberSettings: column,
                      relationSettings: column,
                      translations: [
                        {
                          languageId: selectedLanguage,
                          options: translationOptions,
                        },
                      ],
                    }}
                    readonly={readonly}
                    value={cellValue}
                    disableChange={disableChange}
                    parentOfActiveQuestion={getParentColumn(column)}
                    getResult={(dependsOn) => getDependentCellResult(dependsOn, index)}
                  />
                  {showError && errors.length>0 && column.type !== 'fixed' &&(
                      <s.ErrorWrapper>{errors.includes("mandatory") ? t('MANDATORY'): errors}</s.ErrorWrapper>
                    )
                  }
                </s.TableField>
              );
            })}
          </s.TableFields>
        );
      })}
      {(isAdditionalReportingDocuments && showPhotoFormPreviewRow) ? (
        <s.TableFields>
          <s.TextFieldWrapper
            disabled
            value={`${job.reportNumber}_Photo Form_${factoryName}_${jobStartMonth}_${jobStartDay}&${jobEndDay}_${jobStartYear}`}
          />
          <s.MobileDataNoticeWrapper>
            <s.MobileDataNotice>
              <Trans>PHOTO_FORM_PREVIEW_IN_MOBILE_DATA_TAB</Trans>
            </s.MobileDataNotice>
          </s.MobileDataNoticeWrapper>
        </s.TableFields>
        ) : null}
      {fixedColumns.length === 0 && enableMultipleRows ? (
        <s.TableFieldsAddButtonWrapper data-testid="add-row" onClick={() => addRow()} onKeyDown={() => {}}>
          <s.TableFieldsAdd name="btnAdd" />
          <s.TableFieldsAddLabel><Trans>ADD</Trans></s.TableFieldsAddLabel>
        </s.TableFieldsAddButtonWrapper>
      ) : null}
    </s.TableFieldsWrapper>
  );
};

QuestionTable.propTypes = QuestionTablePropTypes;
QuestionTable.defaultProps = QuestionTableDefaultProps;
